import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Calendar, Building2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { formatAmount } from '@/utils/formatters';
import { TaxBatchModal } from '@/components/TaxBatchModal';
import { useI18n } from '@/hooks/useI18n';
import { HandCoinsIcon } from '@phosphor-icons/react';

interface TaxBatch {
    id: string;
    company_id: string;
    year: number;
    month: number;
    total_amount: number;
    payment_count: number;
    has_linked_payments: boolean;
    created_at: string;
    company?: {
        id: string;
        name: string;
    };
}

interface TaxPayable {
    id: string;
    tax_type: 'personal_income_tax' | 'social_security_contribution';
    amount: number;
    currency: string;
    year: number;
    quarter: number;
    due_date: string;
    employee_id: string;
    is_paid: boolean;
    paid_date?: string | null;
}

function TaxPaymentsPage() {
    const { t } = useI18n();
    const [batches, setBatches] = useState<TaxBatch[]>([]);
    const [taxPayables, setTaxPayables] = useState<TaxPayable[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingPayables, setLoadingPayables] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedBatchId, setSelectedBatchId] = useState<string | undefined>(undefined);
    const [baseCurrency, setBaseCurrency] = useState<string>('HUF');

    useEffect(() => {
        fetchUserData();
        fetchBatches();
        fetchTaxPayables();
    }, []);

    const fetchUserData = async () => {
        try {
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency || 'HUF');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const fetchTaxPayables = async () => {
        setLoadingPayables(true);
        try {
            const response = await apiFetch('/api/people/tax-payables/?is_paid=false&limit=100');
            if (response.ok) {
                const data = await response.json();
                setTaxPayables(data.items || []);
            }
        } catch (error) {
            console.error('Error fetching tax payables:', error);
        } finally {
            setLoadingPayables(false);
        }
    };

    const fetchBatches = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch('/api/finance/tax-batches/?limit=100');
            if (!response.ok) {
                throw new Error(`Failed to fetch tax batches: ${response.status}`);
            }

            const data = await response.json();
            setBatches(data.items || []);
        } catch (err) {
            console.error('Error fetching tax batches:', err);
            setError(err instanceof Error ? err.message : t('taxes.errors.unknown', 'finance'));
        } finally {
            setLoading(false);
        }
    };

    const handleNewBatch = () => {
        setSelectedBatchId(undefined);
        setIsModalOpen(true);
    };

    const handleEditBatch = (batchId: string) => {
        setSelectedBatchId(batchId);
        setIsModalOpen(true);
    };

    const handleDeleteBatch = async (batchId: string) => {
        if (!confirm(t('taxes.deleteConfirm', 'finance'))) {
            return;
        }

        try {
            const response = await apiFetch(`/api/finance/tax-batches/${batchId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                fetchBatches();
            } else {
                const error = await response.json();
                alert(t('taxes.deleteFailed', 'finance', { error: error.detail || t('taxes.deleteFailedFallback', 'finance') }));
            }
        } catch (error) {
            console.error('Error deleting batch:', error);
            alert(t('taxes.deleteFailedFallback', 'finance'));
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedBatchId(undefined);
    };

    const handleModalSave = () => {
        fetchBatches();
    };

    const formatMonthYear = (year: number, month: number): string => {
        const date = new Date(year, month - 1);
        return date.toLocaleDateString(t('taxes.locale', 'finance'), { year: 'numeric', month: 'long' });
    };

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <HandCoinsIcon size={32} weight="duotone" />
                            {t('taxes.subtitle', 'finance')}
                        </h1>
                        <p className="mt-1 text-muted-foreground">
                            {t('taxes.description', 'finance')}
                        </p>
                    </div>
                    <Button onClick={handleNewBatch}>
                        <Plus className="h-4 w-4 mr-2" />
                        {t('taxes.new', 'finance')}
                    </Button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                        {error}
                    </div>
                )}

                {/* Employee Tax Payables Section */}
                <div className="mb-8">
                    <h2 className="text-xl font-semibold mb-4">{t('taxes.upcoming', 'finance')}</h2>
                    {loadingPayables ? (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                            <p className="mt-2 text-gray-600">{t('taxes.loading', 'finance')}</p>
                        </div>
                    ) : taxPayables.length === 0 ? (
                        <div className="border rounded-lg p-8 text-center text-gray-500">
                            {t('taxes.no_unpaid', 'finance')}
                        </div>
                    ) : (
                        <>
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>{t('taxes.tax_type', 'finance')}</TableHead>
                                        <TableHead className="text-right">{t('taxes.total_amount', 'finance')}</TableHead>
                                        <TableHead className="text-right">{t('taxes.count', 'finance')}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(() => {
                                        // Aggregate by tax type
                                        const pitTotal = taxPayables
                                            .filter(p => p.tax_type === 'personal_income_tax')
                                            .reduce((sum, p) => sum + p.amount, 0);
                                        const pitCount = taxPayables.filter(p => p.tax_type === 'personal_income_tax').length;

                                        const sscTotal = taxPayables
                                            .filter(p => p.tax_type === 'social_security_contribution')
                                            .reduce((sum, p) => sum + p.amount, 0);
                                        const sscCount = taxPayables.filter(p => p.tax_type === 'social_security_contribution').length;

                                        const grandTotal = pitTotal + sscTotal;

                                        return (
                                            <>
                                                {pitCount > 0 && (
                                                    <TableRow>
                                                        <TableCell>
                                                            <Badge variant="default">
                                                                {t('taxes.personal_income_tax', 'finance')}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right font-medium">
                                                            {formatAmount(pitTotal, { currencyCode: baseCurrency })}
                                                        </TableCell>
                                                        <TableCell className="text-right text-muted-foreground">
                                                            {t('taxes.paymentsCount', 'finance', { count: pitCount })}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {sscCount > 0 && (
                                                    <TableRow>
                                                        <TableCell>
                                                            <Badge variant="secondary">
                                                                {t('taxes.social_security_contribution', 'finance')}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right font-medium">
                                                            {formatAmount(sscTotal, { currencyCode: baseCurrency })}
                                                        </TableCell>
                                                        <TableCell className="text-right text-muted-foreground">
                                                            {t('taxes.paymentsCount', 'finance', { count: sscCount })}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                <TableRow className="border-t-2 font-semibold bg-muted/50">
                                                    <TableCell>{t('taxes.total', 'finance')}</TableCell>
                                                    <TableCell className="text-right">
                                                        {formatAmount(grandTotal, { currencyCode: baseCurrency })}
                                                    </TableCell>
                                                    <TableCell className="text-right text-muted-foreground">
                                                        {t('taxes.paymentsCount', 'finance', { count: taxPayables.length })}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        );
                                    })()}
                                </TableBody>
                            </Table>
                        </>
                    )}
                </div>

                <h2 className="text-xl font-semibold mb-4">{t('taxes.batches', 'finance')}</h2>

                {/* Loading State */}
                {loading && (
                    <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                        <p className="mt-2 text-gray-600">{t('taxes.loading', 'finance')}</p>
                    </div>
                )}

                {/* Batches Table */}
                {!loading && (
                    <div className="">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('time.month', 'common')}</TableHead>
                                    <TableHead>{t('general.company', 'finance')}</TableHead>
                                    <TableHead className="text-right">{t('general.items', 'finance')}</TableHead>
                                    <TableHead className="text-right">{t('invoices.totalAmount', 'finance')}</TableHead>
                                    <TableHead className="text-center">{t('general.status', 'finance')}</TableHead>
                                    <TableHead className="text-center"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {batches.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                            {t('taxes.no_batches', 'finance')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    batches.map((batch) => (
                                        <TableRow
                                            key={batch.id}
                                            className="cursor-pointer hover:bg-gray-50"
                                            onClick={() => handleEditBatch(batch.id)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium">
                                                        {formatMonthYear(batch.year, batch.month)}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                                    <span>{batch.company?.name || t('taxes.unknown', 'finance')}</span>
                                                </div>
                                            </TableCell>

                                            <TableCell className="text-right">

                                                {batch.payment_count} {batch.payment_count === 1 ? t('general.item', 'finance') : t('general.items', 'finance')}

                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                                {formatAmount(batch.total_amount, { currencyCode: baseCurrency })}
                                            </TableCell>
                                            <TableCell className="text-center">
                                                {batch.has_linked_payments ? (
                                                    <Badge variant="default" className="bg-green-100 text-green-800">
                                                        {t('taxes.linked', 'finance')}
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="secondary">
                                                        {t('taxes.pending', 'finance')}
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleEditBatch(batch.id)}
                                                        title={t('taxes.editBatch', 'finance')}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteBatch(batch.id)
                                                        }}
                                                        disabled={batch.has_linked_payments}
                                                        title={batch.has_linked_payments
                                                            ? t('taxes.deleteBlocked', 'finance')
                                                            : t('taxes.deleteBatch', 'finance')}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {/* Modal */}
                <TaxBatchModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    batchId={selectedBatchId}
                />
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/finance/taxes')({
    component: TaxPaymentsPage,
})
